import { VehicleClass, UseType } from "./form";
import { CURRENCY } from "./formConstants";

// expedition fee has IVA
export const EXPEDITION_FEE = {
  [CURRENCY.crc]: 11978,
  [CURRENCY.usd]: 17.75,
};
export const IVA = 0.13;

export enum Coverage {
  BASIC = "Básico",
  CDF = "Amplio",
}

export enum Payment {
  ANNUAL = "Anual",
  SEMESTRAL = "Semestral",
  TRIMONTHLY = "Trimestral",
  MONTHLY = "Mensual",
}

export interface InsuranceCoverage {
  DM: number | boolean;
  RT: number | boolean;
  GM: number | boolean;
  GF: number | boolean;
  GL: number | boolean;
  AV: number | boolean;
  RP: number | boolean;
  RCP: number | boolean;
  RCB: number | boolean;
  RCO: number | boolean;
  RCC: number | boolean;
  GTPP: number | boolean;
  RA: number | boolean;
  RV: number | boolean;
}

export const variableNames = {
  DM: "Daños Materiales",
  RT: "Robo Total",
  RP: "Robo Parcial",
  RCP: "Responsabilidad Civil Personas",
  RCB: "Responsabilidad Civil Bienes",
  RCO: "Responsabilidad Civil Ocupantes",
  RCC: "Responsabilidad Civil Complementaria",
  GM: "Gastos Médicos de Ocupantes",
  GF: "Gastos Funerarios Conductor",
  RV: "Rotura de Vidrios",
  GL: "Gastos Legales",
  AV: "Asistencia Vial",
  GTPP: "Auto Sustituto",
  RA: "Riesgos Adicionales",
};

export const tableVariables: any = {
  RCP: {
    name: variableNames.RCP,
    description:
      "Cubre los daños que con su vehículo cause a terceras personas.",
    [CURRENCY.crc]: [100000000],
    [CURRENCY.usd]: [200000],
    included: false,
  },
  RCB: {
    name: variableNames.RCB,
    description:
      "Cubre los daños que con su vehículo cause a otros vehículos u objetos de terceros.",
    [CURRENCY.crc]: [20000000],
    [CURRENCY.usd]: [40000],
    included: false,
  },
  RCO: {
    name: variableNames.RCO,
    description:
      "Cubre las indemnizaciones por daños causados a Terceras Personas ocupantes del vehículo asegurado.",
    [CURRENCY.crc]: [5000000],
    [CURRENCY.usd]: [10000],
    included: false,
  },
  RCC: {
    name: variableNames.RCC,
    description:
      "Complementa las coberturas de RC Personas, RC Bienes y RC Ocupantes cuando la indemnización sea mayor a estas.",
    [CURRENCY.crc]: [150000000],
    [CURRENCY.usd]: [200000],
    included: false,
  },
  GM: {
    name: variableNames.GM,
    description:
      "Después de haber agotado la cobertura del SOA (Seguro Obligatorio Automotor), contará con este adicional para cubrir los gastos médicos del conductor y los ocupantes.",
    [CURRENCY.crc]: [10000000, 20000000],
    [CURRENCY.usd]: [21000, 36000],
    included: false,
  },
  GF: {
    name: variableNames.GF,
    description:
      "Ante el fallecimiento del conductor en un accidente con el vehículo asegurado, cubrirá los gastos funerarios que correspondan.",
    [CURRENCY.crc]: [2000000],
    [CURRENCY.usd]: [5000],
    included: false,
  },
  GL: {
    name: variableNames.GL,
    description:
      "Cubre los honorarios de abogados y otros costos, en los casos penales o civiles que lo ameriten.",
    included: true,
  },
  AV: {
    name: variableNames.AV,
    description:
      "Servicio de grúa y cambio de llanta, auxilio por combustible o electricidad, cerrajero. Adicional dentro del GAM: Emergencias Médicas, Best Care y Hit Solution.",
    bike: "Servicio de grúa y auxilio por electricidad.",
    included: true,
  },
  DM: {
    name: variableNames.DM,
    description:
      "Cubre los daños por colisión o vuelco que sufra su vehículo, incluida la pérdida total.",
    [CURRENCY.crc]: [],
    [CURRENCY.usd]: [],
    included: false,
  },
  RT: {
    name: variableNames.RT,
    description: "Cubre el robo o hurto de su vehículo.",
    [CURRENCY.crc]: [],
    [CURRENCY.usd]: [],
    included: false,
  },
  RP: {
    name: variableNames.RP,
    description: "Cubre el robo de partes de su vehículo. ",
    short: "RP",
    [CURRENCY.crc]: [],
    [CURRENCY.usd]: [],
    included: false,
  },
  RV: {
    name: variableNames.RV,
    description:
      "Cubre la sustitución e instalación de cualquier vidrio quebrado en un evento.",
    [CURRENCY.crc]: [],
    [CURRENCY.usd]: [],
    included: false,
  },
  GTPP: {
    name: variableNames.GTPP,
    description:
      "Auto Sustituto mientras su vehículo es reparado a consecuencia de un evento cubierto por el seguro.",
    days: [0, 7, 15, 30],
    included: false,
  },
  RA: {
    name: variableNames.RA,
    description:
      "Cubre los daños causados por la naturaleza, por transportación, vandalismo o alborotos populares.",
    [CURRENCY.crc]: [],
    [CURRENCY.usd]: [],
    included: false,
  },
};

export const coveragePlans = {
  [UseType.PERSONAL]: {
    [Coverage.BASIC]: {
      default: {
        RCP: true,
        RCB: true,
        RCO: true,
        RCC: true,
        GM: true,
        GF: true,
        GL: true,
        AV: true,
        DM: false,
        RT: false,
        RP: false,
        RA: false,
        RV: false,
        GTPP: false,
      },
      [VehicleClass.BIKE]: {
        RCP: true,
        RCB: true,
        RCC: true,
        GL: true,
        AV: true,
        DM: false,
        RA: false,
        RV: false,
        RT: false,
        RP: false,
        RCO: false,
        GM: false,
        GF: false,
        GTPP: false,
      },
    },
    [Coverage.CDF]: {
      default: {
        RCP: true,
        RCB: true,
        RCO: true,
        RCC: true,
        GM: true,
        GF: true,
        GL: true,
        AV: true,
        DM: true,
        RT: true,
        RP: true,
        RA: true,
        RV: true,
        GTPP: true,
      },
      [VehicleClass.BIKE]: {
        RCP: true,
        RCB: true,
        RCC: true,
        GL: true,
        AV: true,
        DM: true,
        RA: true,
        RT: true,
        RP: false,
        RCO: false,
        GM: false,
        GF: false,
        GTPP: false,
        RV: false,
      },
    },
  },
  [UseType.COMMERCIAL]: {
    [Coverage.BASIC]: {
      default: {
        RCP: true,
        RCB: true,
        RCO: true,
        RCC: true,
        GM: true,
        GF: true,
        GL: true,
        AV: true,
        DM: false,
        RT: false,
        RP: false,
        RA: false,
        RV: false,
        GTPP: false,
      },
      [VehicleClass.BIKE]: {
        RCP: true,
        RCB: true,
        RCC: true,
        GL: true,
        AV: true,
        DM: false,
        RA: false,
        RV: false,
        RT: false,
        RP: false,
        RCO: false,
        GM: false,
        GF: false,
        GTPP: false,
      },
    },
    [Coverage.CDF]: {
      default: {
        RCP: true,
        RCB: true,
        RCO: true,
        RCC: true,
        GM: true,
        GF: true,
        GL: true,
        AV: true,
        DM: true,
        RT: true,
        RP: true,
        RA: true,
        RV: true,
        GTPP: false,
      },
      [VehicleClass.BIKE]: {
        RCP: true,
        RCB: true,
        RCC: true,
        GL: true,
        AV: true,
        DM: true,
        RA: true,
        RT: true,
        RP: false,
        RCO: false,
        GM: false,
        GF: false,
        GTPP: false,
        RV: false,
      },
    },
  },
};

export const tableInitialValues: InsuranceCoverage = {
  DM: 0,
  RT: 0,
  GM: 0,
  GF: 0,
  GL: 0,
  AV: 0,
  RP: 0,
  RCP: 0,
  RCB: 0,
  RCO: 0,
  RCC: 0,
  GTPP: 0,
  RA: 0,
  RV: 0,
};

export const deductiblesInitialValues = {
  DM: 0,
  RT: 0,
  RP: 0,
  RCP: 0,
  RCB: 0,
  RA: 0,
  RV: 0,
};

export const paymentInitialValues = {
  semestral: "",
  annual: "",
  monthly: "",
  trimonthly: "",
};
